import React, { useState, useEffect } from 'react';
import { ClockIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { MileageRecord } from '../../types/vehicle';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { formatDate } from '../../utils/dateHelpers';
import { useLanguage } from '../../contexts/LanguageContext';

interface MileageHistoryProps {
  vehicleId: string;
}

const MileageHistory: React.FC<MileageHistoryProps> = ({ vehicleId }) => {
  const { t } = useLanguage();
  const [records, setRecords] = useState<MileageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    loadRecords();
  }, [vehicleId, showAll]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'mileageRecords'),
        where('vehicleId', '==', vehicleId),
        orderBy('date', 'desc'),
        limit(showAll ? 100 : 5)
      );
      
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      })) as MileageRecord[];
      
      setRecords(data);
    } catch (error) {
      console.error('Error loading mileage records:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateDifference = (index: number) => {
    if (index === records.length - 1) return null;
    return records[index].mileage - records[index + 1].mileage;
  };

  const calculateDaysDifference = (index: number) => {
    if (index === records.length - 1) return null;
    const current = records[index].date as Date;
    const previous = records[index + 1].date as Date;
    const diffTime = Math.abs(current.getTime() - previous.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center">
          <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Még nincs km óra előzmény
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
            <ClockIcon className="w-5 h-5 mr-2 text-gray-400" />
            Km óra előzmények ({records.length})
          </h4>
          {records.length > 5 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            >
              {showAll ? 'Kevesebb' : 'Összes megtekintése'}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                Dátum
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                Km óra
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                Változás
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                Megjegyzés
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {records.map((record, index) => {
              const diff = calculateDifference(index);
              const days = calculateDaysDifference(index);
              
              return (
                <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {formatDate(record.date)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-white">
                    {record.mileage.toLocaleString()} km
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                    {diff !== null ? (
                      <div className="flex flex-col items-end">
                        <span className="text-green-600 dark:text-green-400 font-medium">
                          +{diff.toLocaleString()} km
                        </span>
                        {days !== null && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {days} nap
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {record.notes || '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Statistics */}
      {records.length > 1 && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Összesen</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {(records[0].mileage - records[records.length - 1].mileage).toLocaleString()} km
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Átlag / nap</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {(() => {
                  const totalKm = records[0].mileage - records[records.length - 1].mileage;
                  const firstDate = records[records.length - 1].date as Date;
                  const lastDate = records[0].date as Date;
                  const days = Math.ceil((lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24));
                  return days > 0 ? Math.round(totalKm / days).toLocaleString() : 0;
                })()}{' '}
                km
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Rögzítések</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {records.length} db
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MileageHistory;
